import { StandardModal } from 'components/common/Modal/StandardModal';
import { FunctionComponent } from 'react';
import { useSubscriptionFormContext } from '../SubscriptionFormContext';
import {
	Button,
	HStack,
	Label,
	Select,
	SelectOptionType,
	Text,
	VStack,
} from '@montel/montelpro-shared-components-v2';
import { usePermissionsFormContext } from './PermissionsFormContext';
import { AreaCheckboxTree } from './AreaCheckboxTree/AreaCheckboxTree';
import useAuthRoles from 'hooks/useAuthRoles';
import { AdminRole } from 'domain/account/enums';
import { ConditionallyDisabled } from 'components/common/ConditionallyDisabled/ConditionallyDisabled';

const PermissionsModal: FunctionComponent<{
	isOpen: boolean;
	onClose: () => void;
}> = ({ isOpen, onClose }) => {
	const {
		availableApplications,
		availablePermissionSetOptions,
		formState,
		setFormState,
		onSave,
		editingPermissions,
		preSelectedApplication,
		bundleHasAreas,
		companyIsPayingCustomerForSelectedApplication,
	} = usePermissionsFormContext();

	const { selectedApplications } = useSubscriptionFormContext();
	const { adminIsRestrictedToRole: isRestrictedToRole } = useAuthRoles();

	const showRegionsInput =
		availablePermissionSetOptions &&
		availablePermissionSetOptions.areas &&
		availablePermissionSetOptions.areas.length > 0;

	const showApplicationAddonsInput =
		availableApplications.length > 0 &&
		availablePermissionSetOptions &&
		availablePermissionSetOptions.addons &&
		availablePermissionSetOptions.addons.length > 0;

	const isEditing = !!editingPermissions;
	const isSalesRole = isRestrictedToRole(AdminRole.SALES);
	const companyRestrictsAddingApplication =
		isSalesRole &&
		!!formState.application &&
		!companyIsPayingCustomerForSelectedApplication;

	return (
		<StandardModal
			isOpen={isOpen}
			onClose={onClose}
			header={isEditing ? 'Edit Permissions' : 'Add Permissions'}
			footer={
				<HStack fullWidth justify="flex-end" gap="lg">
					<ConditionallyDisabled
						disableIf={companyRestrictsAddingApplication}
						tooltip="Sales role can only add a product the company already has an active paid subscription for"
					>
						<Button
							text={
								isEditing ? 'Save changes' : 'Add permissions'
							}
							onClick={() => {
								onSave();
								onClose();
							}}
							variant="primary"
							isDisabled={
								!formState.application || !formState.plan
							}
						/>
					</ConditionallyDisabled>
					<Button
						text="Cancel"
						variant="tertiary"
						onClick={onClose}
					/>
				</HStack>
			}
		>
			<>
				{availableApplications && (
					<VStack gap="xl">
						{selectedApplications?.length ==
							availableApplications?.length && (
							<Text size="xs">
								You have selected all available products
							</Text>
						)}
						<HStack fullWidth gap="md" wrap="nowrap">
							<Select
								label="Product"
								options={availableApplications}
								required
								disabled={isEditing || preSelectedApplication}
								onChange={(
									selectedApplication: SelectOptionType,
								) =>
									setFormState({
										application: selectedApplication,
										plan: null,
										addons: [],
										countries: [],
										permissionsTemplate: null,
									})
								}
								value={formState.application}
							/>
							{availablePermissionSetOptions && (
								<Select
									label="Plan"
									placeholder={
										!formState.application
											? 'Select a product first...'
											: 'Select a plan...'
									}
									options={
										availablePermissionSetOptions?.plans ||
										[]
									}
									required
									onChange={(selectedPlan) =>
										setFormState({
											...formState,
											plan: selectedPlan,
										})
									}
									disabled={!formState.application}
									value={formState.plan}
								/>
							)}
							{showApplicationAddonsInput && (
								<Select
									label="Addons"
									options={
										availablePermissionSetOptions?.addons ||
										[]
									}
									onChange={(options) =>
										setFormState({
											...formState,
											addons: options,
										})
									}
									isMulti
									value={formState.addons}
									variant="checkbox"
								/>
							)}
						</HStack>
						<HStack wrap="nowrap" gap="md" fullWidth>
							{showRegionsInput && !bundleHasAreas && (
								<VStack fullWidth gap="xs">
									<Label text="Areas" />
									<AreaCheckboxTree
										initialState={formState.countries}
										availableAreas={
											availablePermissionSetOptions.areas
										}
										onChange={(countries: string[]) =>
											setFormState({
												...formState,
												countries,
											})
										}
									/>
								</VStack>
							)}
							{showRegionsInput && bundleHasAreas && (
								<Text size="xs" color="textSubtle">
									Areas are inherited from the bundle and
									cannot be set on individual subscriptions.
								</Text>
							)}
						</HStack>
					</VStack>
				)}
			</>
		</StandardModal>
	);
};

export default PermissionsModal;
